import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

const green = "#43BD98";
const accent = "#D9B8CE";

const containerVariants = {
	hidden: {},
	show: { transition: { staggerChildren: 0.15 } },
};

const cardVariants = {
	hidden: { opacity: 0, y: 25, scale: 0.98 },
	show: {
		opacity: 1,
		y: 0,
		scale: 1,
		transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] },
	},
};

export default function HowItWorks2() {
	const { t } = useTranslation();

	const steps = Array.from({ length: 4 }, (_, i) => ({
		number: i + 1,
		title: t(`salon2.howItWorks.steps.${i}.title`),
		text: t(`salon2.howItWorks.steps.${i}.text`),
	}));

	return (
		<section
			id="como-funciona"
			className="py-24 overflow-hidden"
			style={{
				backgroundColor: "#fff9fc",
				fontFamily: "'Poppins', sans-serif",
			}}
		>
			<div className="max-w-7xl mx-auto px-6 lg:px-16">
				{/* Header */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.7, ease: "easeOut" }}
					viewport={{ once: true, amount: 0.3 }}
					className="text-center mb-16"
				>
					<p
						className="uppercase tracking-wider text-sm font-semibold mb-3"
						style={{ color: accent }}
					>
						{t("salon2.howItWorks.badge")}
					</p>
					<h2
						style={{
							fontSize: "clamp(1.8rem, 5vw, 2.8rem)",
							color: "#1a1a1a",
							fontWeight: 800,
							margin: 0,
						}}
					>
						{t("salon2.howItWorks.title")}
					</h2>
				</motion.div>

				{/* Steps */}
				<motion.div
					variants={containerVariants}
					initial="hidden"
					whileInView="show"
					viewport={{ once: true, amount: 0.2 }}
					className="relative"
				>
					{/* Connector line (desktop) */}
					<div
						className="hidden lg:block absolute top-[52px] left-[10%] right-[10%] h-[2px]"
						style={{
							background: `linear-gradient(90deg, ${green}30, ${green}60, ${green}30)`,
						}}
					/>

					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
						{steps.map((step) => (
							<motion.div
								key={step.number}
								variants={cardVariants}
								whileHover={{
									y: -6,
									scale: 1.02,
									boxShadow: "0 10px 30px rgba(67,189,152,0.12)",
									transition: { duration: 0.25 },
								}}
								className="relative bg-white rounded-2xl p-8 text-center border border-gray-100 shadow-[0_8px_24px_rgba(0,0,0,0.03)] transition-all duration-300"
							>
								<div
									className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5 relative z-10"
									style={{
										backgroundColor: green,
										color: "#fff",
										fontWeight: 700,
										fontSize: "1.2rem",
										boxShadow: "0 4px 12px rgba(67,189,152,0.3)",
									}}
								>
									{step.number}
								</div>
								<h3
									className="font-semibold text-lg mb-2"
									style={{ color: "#222" }}
								>
									{step.title}
								</h3>
								<p
									className="leading-relaxed"
									style={{
										color: "#555",
										fontSize: "0.95rem",
									}}
								>
									{step.text}
								</p>
							</motion.div>
						))}
					</div>
				</motion.div>

				{/* Help note */}
				<motion.p
					initial={{ opacity: 0, y: 15 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6, delay: 0.3 }}
					viewport={{ once: true }}
					className="text-center mt-12"
					style={{ color: "#888", fontSize: "0.95rem" }}
				>
					{t("salon2.howItWorks.note")}
				</motion.p>
			</div>
		</section>
	);
}
